import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { FiscalTaxProfile } from '@aeris/contracts'
import {
  Badge,
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Section,
  Select,
} from '@aeris/ui'

import {
  ApiError,
  createFiscalTaxProfile,
  fetchFiscalTaxProfiles,
  updateFiscalTaxProfile,
} from '../lib/api.ts'

/**
 * Os perfis de imposto (F373): o que vale para um grupo de produtos na nota. Quem
 * diz os números é o contador. A tela só oferece os CSOSN que o Aeris já emite.
 */

const FISCAL_TAX_PROFILES_KEY = ['fiscal-tax-profiles']

const CSOSN_LABELS = {
  '102': '102 — Tributada pelo Simples, sem crédito',
  '103': '103 — Isenção do ICMS no Simples',
  '300': '300 — Imune',
  '400': '400 — Não tributada pelo Simples',
  '500': '500 — ICMS já cobrado por substituição tributária',
} as const

type Csosn = keyof typeof CSOSN_LABELS

const ProfileDialog = ({
  profile,
  onClose,
}: {
  profile: FiscalTaxProfile | null
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [name, setName] = useState(profile?.name ?? '')
  const [csosn, setCsosn] = useState<Csosn>(
    (profile?.csosn as Csosn | undefined) ?? '102'
  )
  const [origin, setOrigin] = useState(profile?.origin ?? '0')
  const [cfopInternal, setCfopInternal] = useState(profile?.cfopInternal ?? '')
  const [cfopInterstate, setCfopInterstate] = useState(
    profile?.cfopInterstate ?? ''
  )
  const [cest, setCest] = useState(profile?.cest ?? '')
  const [pisCst, setPisCst] = useState(profile?.pisCst ?? '99')
  const [cofinsCst, setCofinsCst] = useState(profile?.cofinsCst ?? '99')
  const [archived, setArchived] = useState(profile?.archived ?? false)

  const save = useMutation({
    meta: { errorShownInline: true },
    mutationFn: async () => {
      const fields = {
        name: name.trim(),
        origin,
        csosn,
        pisCst,
        cofinsCst,
        cest: cest.trim() === '' ? null : cest.trim(),
        cfopInternal,
        cfopInterstate,
      }
      return profile === null
        ? await createFiscalTaxProfile(fields)
        : await updateFiscalTaxProfile(profile.id, {
            ...fields,
            archived,
            version: profile.version,
          })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: FISCAL_TAX_PROFILES_KEY,
      })
      onClose()
    },
  })

  const digits =
    (setter: (value: string) => void, length: number) =>
    (event: { target: { value: string } }) => {
      setter(event.target.value.replace(/\D/gu, '').slice(0, length))
    }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={profile === null ? 'Novo perfil de imposto' : 'Alterar perfil'}
        description="Peça estes códigos ao contador."
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <Field label="Nome do perfil" hint="Ex.: Periféricos de informática">
            {(field) => (
              <Input
                {...field}
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                }}
              />
            )}
          </Field>
          <Field label="CSOSN">
            {(field) => (
              <Select
                {...field}
                value={csosn}
                onChange={(event) => {
                  setCsosn(event.target.value as Csosn)
                }}
              >
                {(Object.keys(CSOSN_LABELS) as Csosn[]).map((value) => (
                  <option key={value} value={value}>
                    {CSOSN_LABELS[value]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="flex flex-wrap gap-3">
            <Field label="CFOP no estado" className="min-w-32 flex-1">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  placeholder="5102"
                  value={cfopInternal}
                  onChange={digits(setCfopInternal, 4)}
                />
              )}
            </Field>
            <Field label="CFOP fora do estado" className="min-w-32 flex-1">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  placeholder="6102"
                  value={cfopInterstate}
                  onChange={digits(setCfopInterstate, 4)}
                />
              )}
            </Field>
            <Field
              label="CEST"
              hint="Só se tiver ST."
              className="min-w-32 flex-1"
            >
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={cest}
                  onChange={digits(setCest, 7)}
                />
              )}
            </Field>
          </div>
          <div className="flex flex-wrap gap-3">
            <Field label="Origem" className="w-24">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={origin}
                  onChange={digits(setOrigin, 1)}
                />
              )}
            </Field>
            <Field label="CST do PIS" className="w-28">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={pisCst}
                  onChange={digits(setPisCst, 2)}
                />
              )}
            </Field>
            <Field label="CST da COFINS" className="w-28">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={cofinsCst}
                  onChange={digits(setCofinsCst, 2)}
                />
              )}
            </Field>
          </div>
          {profile === null ? null : (
            <CheckboxField
              label="Arquivado"
              checked={archived}
              onCheckedChange={(checked) => {
                setArchived(checked === true)
              }}
            />
          )}
          {save.isError ? (
            <InlineAlert tone="danger">
              {save.error instanceof ApiError
                ? save.error.message
                : 'Não foi possível salvar agora.'}
            </InlineAlert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Voltar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={save.isPending}
              disabled={
                name.trim() === '' ||
                cfopInternal.length !== 4 ||
                cfopInterstate.length !== 4 ||
                origin === ''
              }
            >
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export const FiscalTaxProfiles = (): ReactNode => {
  const profiles = useQuery({
    queryKey: FISCAL_TAX_PROFILES_KEY,
    queryFn: ({ signal }) => fetchFiscalTaxProfiles(signal),
  })
  const [editing, setEditing] = useState<FiscalTaxProfile | 'new' | null>(null)
  const list = profiles.data?.profiles ?? []

  return (
    <Section
      title="Perfis de imposto"
      description="O imposto de cada grupo de produtos na nota de produto."
    >
      <div className="flex flex-col gap-3">
        {list.length === 0 ? (
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            Nenhum perfil cadastrado ainda.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.map((profile) => (
              <li
                key={profile.id}
                className="flex flex-wrap items-center gap-3"
              >
                <span className="font-medium">{profile.name}</span>
                <span className="text-(length:--text-sm) text-(--text-secondary)">
                  CSOSN {profile.csosn}, CFOP {profile.cfopInternal}
                </span>
                {profile.archived ? (
                  <Badge tone="neutral">Arquivado</Badge>
                ) : null}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditing(profile)
                  }}
                >
                  Alterar
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div>
          <Button
            onClick={() => {
              setEditing('new')
            }}
          >
            Novo perfil
          </Button>
        </div>
      </div>
      {editing === null ? null : (
        <ProfileDialog
          profile={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null)
          }}
        />
      )}
    </Section>
  )
}
